import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Check, X, Filter, Calendar, Pill, FileSignature, Activity,
  ChevronDown, ChevronUp, Loader2, CheckCircle2, Circle,
  Clock, Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${window.__WR_API_BASE__}/api`;

const OrdersReview = ({ onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(14); // Default 14 days
  const [filterType, setFilterType] = useState('all'); // all, referral, imaging, medication
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, submitted
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc'); // date_desc, date_asc, type
  const [updating, setUpdating] = useState({});
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/history/orders-review?days=${dateRange}`, {
          withCredentials: true
        });
        setOrders(response.data.orders || []);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        toast.error('Failed to load orders');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [dateRange]);

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    // Filter by type
    if (filterType !== 'all') {
      result = result.filter(o => o.type === filterType);
    }
    
    // Filter by status
    if (filterStatus === 'pending') {
      result = result.filter(o => !o.ehr_submitted);
    } else if (filterStatus === 'submitted') {
      result = result.filter(o => o.ehr_submitted);
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(o => 
        (o.specialty && o.specialty.toLowerCase().includes(query)) ||
        (o.imaging_type && o.imaging_type.toLowerCase().includes(query)) ||
        (o.name && o.name.toLowerCase().includes(query)) ||
        
        (o.indication && o.indication.toLowerCase().includes(query))
      );
    }
    
    // Sort
    result.sort((a, b) => {
      if (sortBy === 'date_desc') {
        return new Date(b.timestamp) - new Date(a.timestamp);
      } else if (sortBy === 'date_asc') {
        return new Date(a.timestamp) - new Date(b.timestamp);
      } else if (sortBy === 'type') {
        return a.type.localeCompare(b.type);
      }
      return 0;
    });
    
    return result;
  }, [orders, filterType, filterStatus, searchQuery, sortBy]);

  // Mark order as submitted to EHR
  const markSubmitted = async (order, submitted) => {
    const orderId = order.id;
    setUpdating(prev => ({ ...prev, [orderId]: true }));
    
    try {
      // Parse order ID to get history_id and index
      const parts = order.id.split('_');
      const historyId = parts.slice(0, -2).join('_');
      const orderType = parts[parts.length - 2];
      const orderIndex = parseInt(parts[parts.length - 1]);
      
      await axios.post(`${API}/history/mark-ehr-submitted`, {
        history_id: historyId,
        order_type: orderType === 'ref' ? 'referral' : orderType === 'img' ? 'imaging' : 'medication',
        order_index: orderIndex,
        submitted
      }, { withCredentials: true });
      
      // Update local state
      setOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, ehr_submitted: submitted } : o
      ));
      
      toast.success(submitted ? 'Marked as submitted to EHR' : 'Marked as pending');
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update');
    } finally {
      setUpdating(prev => ({ ...prev, [orderId]: false }));
    }
  };

  // Stats
  const stats = useMemo(() => {
    const total = orders.length;
    const submitted = orders.filter(o => o.ehr_submitted).length;
    const pending = total - submitted;
    const referrals = orders.filter(o => o.type === 'referral').length;
    const imaging = orders.filter(o => o.type === 'imaging').length;
    const meds = orders.filter(o => o.type === 'medication').length;
    return { total, submitted, pending, referrals, imaging, meds };
  }, [orders]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'referral': return <FileSignature className="w-4 h-4 text-blue-400" />;
      case 'imaging': return <Activity className="w-4 h-4 text-purple-400" />;
      case 'medication': return <Pill className="w-4 h-4 text-green-400" />;
      default: return null;
    }
  };

  const getTypeLabel = (order) => {
    switch (order.type) {
      case 'referral': return order.specialty;
      case 'imaging': return `${order.imaging_type}${order.body_part ? ` - ${order.body_part}` : ''}`;
      case 'medication': return order.name;
      default: return '';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-auto">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Orders Review</h1>
            <p className="text-sm text-muted-foreground">Review and track EHR submissions {dateRange === 1 ? 'from today' : `for the last ${dateRange} days`}</p>
          </div>
          <Button variant="outline" onClick={onClose} data-testid="close-orders-review">
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>

        {/* Stats Bar — Clickable tiles to quick-filter */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
          <button
            onClick={() => { setFilterType('all'); setFilterStatus('all'); }}
            className={`rounded-lg border p-3 text-center transition-all ${filterType === 'all' && filterStatus === 'all' ? 'bg-primary/10 border-primary/50 ring-2 ring-primary/30' : 'bg-card/50 border-border hover:border-primary/30'}`}
            data-testid="stat-total"
          >
            <div className="text-2xl font-bold text-foreground">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total</div>
          </button>
          <button
            onClick={() => { setFilterType('all'); setFilterStatus('pending'); }}
            className={`rounded-lg border p-3 text-center transition-all ${filterStatus === 'pending' ? 'bg-amber-500/20 border-amber-500/50 ring-2 ring-amber-500/30' : 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20'}`}
            data-testid="stat-pending"
          >
            <div className="text-2xl font-bold text-amber-400">{stats.pending}</div>
            <div className="text-xs text-amber-400/80">Pending</div>
          </button>
          <button
            onClick={() => { setFilterType('all'); setFilterStatus('submitted'); }}
            className={`rounded-lg border p-3 text-center transition-all ${filterStatus === 'submitted' ? 'bg-green-500/20 border-green-500/50 ring-2 ring-green-500/30' : 'bg-green-500/10 border-green-500/30 hover:bg-green-500/20'}`}
            data-testid="stat-submitted"
          >
            <div className="text-2xl font-bold text-green-400">{stats.submitted}</div>
            <div className="text-xs text-green-400/80">Submitted</div>
          </button>
          <button
            onClick={() => { setFilterType('referral'); setFilterStatus('all'); }}
            className={`rounded-lg border p-3 text-center transition-all ${filterType === 'referral' ? 'bg-blue-500/20 border-blue-500/50 ring-2 ring-blue-500/30' : 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20'}`}
            data-testid="stat-referrals"
          >
            <div className="text-2xl font-bold text-blue-400">{stats.referrals}</div>
            <div className="text-xs text-blue-400/80">Referrals</div>
          </button>
          <button
            onClick={() => { setFilterType('imaging'); setFilterStatus('all'); }}
            className={`rounded-lg border p-3 text-center transition-all ${filterType === 'imaging' ? 'bg-purple-500/20 border-purple-500/50 ring-2 ring-purple-500/30' : 'bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20'}`}
            data-testid="stat-imaging"
          >
            <div className="text-2xl font-bold text-purple-400">{stats.imaging}</div>
            <div className="text-xs text-purple-400/80">Imaging</div>
          </button>
          <button
            onClick={() => { setFilterType('medication'); setFilterStatus('all'); }}
            className={`rounded-lg border p-3 text-center transition-all ${filterType === 'medication' ? 'bg-green-500/20 border-green-500/50 ring-2 ring-green-500/30' : 'bg-green-500/10 border-green-500/30 hover:bg-green-500/20'}`}
            data-testid="stat-meds"
          >
            <div className="text-2xl font-bold text-green-400">{stats.meds}</div>
            <div className="text-xs text-green-400/80">Meds</div>
          </button>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="orders-search"
                />
              </div>
              
              {/* Date Range */}
              <Select value={dateRange.toString()} onValueChange={(v) => setDateRange(parseInt(v))}>
                <SelectTrigger className="w-[130px] h-9" data-testid="date-range-filter">
                  <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Today</SelectItem>
                  <SelectItem value="3">Last 3 days</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Type Filter */}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[130px] h-9" data-testid="type-filter">
                  <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="referral">Referrals</SelectItem>
                  <SelectItem value="imaging">Imaging</SelectItem>
                  <SelectItem value="medication">Medications</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[130px] h-9" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] h-9" data-testid="sort-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Newest First</SelectItem>
                  <SelectItem value="date_asc">Oldest First</SelectItem>
                  <SelectItem value="type">By Type</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No orders found for the selected filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredOrders.map((order) => (
              <Card 
                key={order.id} 
                className={`transition-all cursor-pointer ${order.ehr_submitted ? 'bg-green-500/5 border-green-500/20' : 'bg-card hover:bg-card/80 active:bg-primary/5'}`}
                data-testid={`order-item-${order.id}`}
                onClick={() => setExpandedOrders(prev => {
                  const next = new Set(prev);
                  if (next.has(order.id)) next.delete(order.id); else next.add(order.id);
                  return next;
                })}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={(e) => { e.stopPropagation(); markSubmitted(order, !order.ehr_submitted); }}
                      disabled={updating[order.id]}
                      className="shrink-0"
                      data-testid={`order-checkbox-${order.id}`}
                    >
                      {updating[order.id] ? (
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      ) : order.ehr_submitted ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground hover:text-primary" />
                      )}
                    </button>
                    
                    {/* Type Icon */}
                    <div className="shrink-0">
                      {getTypeIcon(order.type)}
                    </div>
                    
                    {/* Order Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium ${order.ehr_submitted ? 'text-green-400 line-through' : 'text-foreground'}`}>
                          {getTypeLabel(order)}
                        </span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {order.type}
                        </Badge>
                        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${expandedOrders.has(order.id) ? 'rotate-180' : ''}`} />
                      </div>
                      {!expandedOrders.has(order.id) && order.indication && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{order.indication}</p>
                      )}
                    </div>
                    
                    {/* Date */}
                    <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(order.timestamp)}</span>
                    </div>
                  </div>

                  {/* Expanded content */}
                  {expandedOrders.has(order.id) && order.indication && (
                    <div className="mt-3 pl-14 space-y-2" onClick={(e) => e.stopPropagation()}>
                      <div className="p-3 bg-secondary/30 rounded-lg text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                        {order.indication}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(order.indication);
                              toast.success('Copied!');
                            } catch { toast.error('Copy failed'); }
                          }}
                          data-testid={`copy-order-${order.id}`}
                        >
                          Copy
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className={`h-7 text-xs ${order.ehr_submitted ? 'text-green-500 border-green-500/30' : 'text-amber-400 border-amber-500/30'}`}
                          onClick={() => markSubmitted(order, !order.ehr_submitted)}
                          disabled={updating[order.id]}
                        >
                          {order.ehr_submitted ? 'Undo Submit' : 'Mark Submitted'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Summary Footer */}
        {!loading && filteredOrders.length > 0 && (
          <div className="mt-4 text-center text-sm text-muted-foreground">
            Showing {filteredOrders.length} of {orders.length} orders • 
            {stats.pending > 0 && <span className="text-amber-400 ml-1">{stats.pending} pending</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersReview;
